// Catálogo público de Secretario: categorías, conectores, herramientas y ejemplos.
// Es la única fuente de la portada, Funcionalidades, el centro de ayuda y la tabla de permisos.
//
// Describe solo lo que Secretario hace hoy (repo `secretario`: `conector-google` y las herramientas
// del sistema; en palabras del usuario, como `docs/manual-usuario.html`). Para sumar un conector
// basta agregar un objeto a CONNECTORS; si su categoría es nueva, también a CATEGORIES.
//
// Forma de un conector:
//   id            slug único; es la URL de su guía: /secretario/aprende/<id>
//   name          como lo ve el usuario
//   category      id de una categoría de CATEGORIES
//   status        'available' (disponible) o 'planned' (en la hoja de ruta, sin fecha)
//   icon, color   ícono de components/icon.js y color del logo
//   summary       una frase de qué hace
//   video         null mientras no exista, o { src (mp4 propio) | embed (URL de un reproductor),
//                 poster, duration ('m:ss'), title, description }
//   capabilities  qué puedes hacer, en viñetas
//   tools         [{ id (nombre interno), name, description, effect (pide tu «sí»), examples: [...] }]
//   requirements  qué necesitas antes de usarlo
//   scopes        permisos de Google: [{ scope, access, use }]
//   setup         pasos para conectarlo
//   notes         consideraciones importantes
//   links         [{ label, href }]; href interno empieza por '/'

export const CATEGORIES = [
  {
    id: 'correo',
    name: 'Correo',
    icon: 'mail',
    blurb: 'Redacta y envía correos sin abrir tu bandeja.',
  },
  {
    id: 'calendario',
    name: 'Calendario',
    icon: 'calendar',
    blurb: 'Consulta tu agenda, encuentra huecos y agenda reuniones con videollamada.',
  },
  {
    id: 'tareas',
    name: 'Tareas',
    icon: 'tasks',
    blurb: 'Anota pendientes, organízalos en listas y márcalos como hechos.',
  },
  {
    id: 'contactos',
    name: 'Contactos',
    icon: 'users',
    blurb: 'Encuentra el correo o el teléfono de alguien y guarda contactos nuevos.',
  },
  {
    id: 'documentos',
    name: 'Documentos',
    icon: 'file',
    blurb: 'Crea documentos, hojas de cálculo y formularios, y compártelos.',
  },
  {
    id: 'asistente',
    name: 'Recordatorios',
    icon: 'bell',
    blurb: 'Avisos, encargos programados y tus preferencias. Vienen incluidos.',
  },
]

// Pasos y requisitos comunes a los conectores de Google.
const GOOGLE_REQUIREMENTS = [
  'Tener acceso a Secretario: tu número de WhatsApp registrado.',
  'Una cuenta de Google (personal o de Google Workspace, si tu organización permite conectar aplicaciones de terceros).',
  'Que el conector esté asignado a tu cuenta. Si pides algo que no tienes, Secretario te lo dice.',
]

const GOOGLE_SETUP = [
  'Escríbele a Secretario «conecta mi cuenta de Google».',
  'Abre el enlace que te envía. Dura poco y es solo tuyo; si se vence, pide otro.',
  'Entra con tu cuenta de Google y acepta los permisos. Si cancelas en esa pantalla, no se conecta nada.',
  'Secretario te confirma en el chat que la cuenta quedó lista. Desde ahí, pídele lo que necesites.',
]

const GOOGLE_LINKS = [
  { label: 'Permisos y privacidad de Secretario', href: '/secretario#permisos' },
  { label: 'Revisar o quitar el acceso en tu cuenta de Google', href: 'https://myaccount.google.com/permissions' },
  { label: 'Política de privacidad', href: '/politica-de-privacidad' },
]

// Solo ve los archivos que él mismo creó (scope drive.file).
const DRIVE_NOTE =
  'En tu Drive, Secretario solo abre los documentos, hojas y formularios que él mismo creó. Tus otros archivos quedan fuera de su alcance.'

const CONFIRM_NOTE =
  'Antes de crear, cambiar, enviar o borrar algo, Secretario te muestra qué va a hacer y espera tu «sí» en el siguiente mensaje.'

const DRIVE_FILE_SCOPE = {
  scope: 'drive.file',
  access: 'Solo los archivos que crea Secretario',
  use: 'Crea, lee, comparte y adjunta en PDF los documentos, hojas y formularios que le pides. No ve el resto de tu Drive.',
}

export const CONNECTORS = [
  {
    id: 'gmail',
    name: 'Gmail',
    category: 'correo',
    status: 'available',
    icon: 'mail',
    color: '#d93025',
    summary: 'Envía correos desde tu cuenta de Gmail con solo decir a quién y qué.',
    video: null,
    capabilities: [
      'Redactar y enviar correos a quien le indiques, con copia y copia oculta.',
      'Adjuntar en PDF los documentos y hojas que Secretario creó.',
      'Ajustar el texto antes de enviarlo: más corto, más formal o con otro asunto.',
    ],
    tools: [
      {
        id: 'enviar_correo',
        name: 'Enviar correo',
        description: 'Escribe y envía un correo de texto desde tu Gmail. Te muestra el destinatario, el asunto y el texto antes de enviarlo.',
        effect: true,
        examples: [
          'Escríbele a Laura que llego 10 minutos tarde a la reunión',
          'Envíale a Pedro el informe de ventas en PDF',
          'Mándale a Carlos un correo confirmando la reunión de mañana, con copia a Ana',
        ],
      },
    ],
    requirements: GOOGLE_REQUIREMENTS,
    scopes: [
      { scope: 'gmail.send', access: 'Enviar correos en tu nombre', use: 'Envía los correos que le pides y confirmas. No puede leer, buscar ni borrar los correos de tu bandeja.' },
      DRIVE_FILE_SCOPE,
    ],
    setup: GOOGLE_SETUP,
    notes: [
      'Secretario envía correos, pero todavía no lee ni busca los que recibes.',
      'Los correos van en texto plano, sin formato.',
      'Solo adjunta documentos y hojas que Secretario creó.',
      'Puedes enseñarle tu estilo: «de ahora en adelante, mis correos siempre formales y firmados como Ana».',
      CONFIRM_NOTE,
    ],
    links: GOOGLE_LINKS,
  },
  {
    id: 'google-calendar',
    name: 'Google Calendar',
    category: 'calendario',
    status: 'available',
    icon: 'calendar',
    color: '#1a73e8',
    summary: 'Consulta tu agenda y crea, mueve o cancela reuniones, con Google Meet si lo pides.',
    video: null,
    capabilities: [
      'Ver tu agenda de un día, una semana o un rango de fechas.',
      'Encontrar horas libres para una reunión.',
      'Crear eventos con invitados, ubicación, Meet y repeticiones.',
      'Mover, cambiar o cancelar eventos, y responder invitaciones.',
    ],
    tools: [
      { id: 'ver_agenda', name: 'Ver agenda', description: 'Te dice qué eventos tienes entre dos fechas.', effect: false, examples: ['¿Qué tengo mañana?', '¿Cómo está mi semana?'] },
      { id: 'buscar_eventos', name: 'Buscar eventos', description: 'Encuentra eventos por título, descripción, lugar o invitados.', effect: false, examples: ['¿Cuándo es la reunión con Juan?', 'Busca los eventos del proyecto Atlas de este mes'] },
      { id: 'ver_horas_libres', name: 'Encontrar horas libres', description: 'Busca huecos libres de la duración que necesitas.', effect: false, examples: ['Busca un hueco de una hora el viernes en la tarde'] },
      { id: 'crear_evento', name: 'Crear evento', description: 'Agenda un evento, invita a otras personas, agrega Meet o lo repite.', effect: true, examples: ['Agenda una reunión con Ana el jueves a las 10 y agrega Meet', 'Crea el comité de ventas todos los lunes a las 9'] },
      { id: 'actualizar_evento', name: 'Cambiar evento', description: 'Mueve un evento o cambia su título, invitados, lugar o recordatorios.', effect: true, examples: ['Mueve la reunión con Juan a las 4 y agrega Meet'] },
      { id: 'cancelar_evento', name: 'Cancelar evento', description: 'Borra un evento y Google avisa a los invitados.', effect: true, examples: ['Cancela la reunión de mañana a las 3'] },
      { id: 'responder_invitacion', name: 'Responder invitación', description: 'Acepta, rechaza o deja en «quizás» una invitación que te enviaron.', effect: true, examples: ['Acepta la invitación al comité del martes'] },
      { id: 'ver_calendarios', name: 'Ver calendarios', description: 'Lista tus calendarios para usar uno distinto del principal.', effect: false, examples: ['¿Qué calendarios tengo?'] },
    ],
    requirements: GOOGLE_REQUIREMENTS,
    scopes: [
      { scope: 'calendar.events', access: 'Ver y editar los eventos de tus calendarios', use: 'Consulta tu agenda y crea, actualiza o cancela los eventos que le pides, con Google Meet si lo indicas.' },
      { scope: 'calendar.calendarlist.readonly', access: 'Ver la lista de tus calendarios', use: 'Sabe qué calendarios tienes para usar el que le indiques. No los cambia.' },
    ],
    setup: GOOGLE_SETUP,
    notes: [
      'Las horas se leen en tu zona horaria. Si te mudas, avísale al administrador.',
      'Con invitados, Google les envía la invitación y les avisa de cambios o cancelaciones.',
      'En un evento que se repite, puedes cambiar solo una fecha o toda la serie.',
      CONFIRM_NOTE,
    ],
    links: GOOGLE_LINKS,
  },
  {
    id: 'google-tasks',
    name: 'Google Tasks',
    category: 'tareas',
    status: 'available',
    icon: 'tasks',
    color: '#1967d2',
    summary: 'Crea, cambia, mueve y completa tus tareas y listas de Google Tasks.',
    video: null,
    capabilities: [
      'Anotar tareas con fecha límite y notas, o como subtareas.',
      'Consultar tus pendientes por lista.',
      'Marcar tareas como hechas, cambiarlas, moverlas o borrarlas.',
      'Crear y borrar listas de tareas.',
    ],
    tools: [
      { id: 'ver_tareas', name: 'Ver tareas', description: 'Te muestra las tareas pendientes de una lista, o también las completadas.', effect: false, examples: ['¿Qué pendientes tengo?'] },
      { id: 'ver_listas_tareas', name: 'Ver listas', description: 'Te dice qué listas de tareas tienes.', effect: false, examples: ['¿Qué listas de tareas tengo?'] },
      { id: 'crear_tarea', name: 'Crear tarea', description: 'Anota una tarea con fecha límite y notas, o como subtarea de otra.', effect: true, examples: ['Anota comprar el regalo de mamá para el sábado', 'Agrega a mis pendientes renovar el seguro antes del 15'] },
      { id: 'completar_tarea', name: 'Completar tarea', description: 'Marca una tarea como hecha.', effect: true, examples: ['Marca como hecha la del banco'] },
      { id: 'actualizar_tarea', name: 'Cambiar tarea', description: 'Cambia el título, las notas o la fecha, o reabre una tarea.', effect: true, examples: ['Cambia la fecha de renovar el seguro al 20'] },
      { id: 'mover_tarea', name: 'Mover tarea', description: 'Pasa una tarea a otra lista o la vuelve subtarea de otra.', effect: true, examples: ['Pasa la tarea del informe a la lista Trabajo'] },
      { id: 'borrar_tarea', name: 'Borrar tarea', description: 'Elimina una tarea de una lista.', effect: true, examples: ['Borra la tarea de llamar al plomero'] },
      { id: 'crear_lista_tareas', name: 'Crear lista', description: 'Crea una lista de tareas nueva.', effect: true, examples: ['Crea una lista llamada Viaje a Cartagena'] },
      { id: 'borrar_lista_tareas', name: 'Borrar lista', description: 'Borra una lista con todas sus tareas. La lista principal no se puede borrar.', effect: true, examples: ['Borra la lista Viaje a Cartagena'] },
    ],
    requirements: GOOGLE_REQUIREMENTS,
    scopes: [
      { scope: 'tasks', access: 'Ver y editar tus tareas', use: 'Consulta tus listas y tareas, y crea, cambia, mueve, completa o borra las que le indiques.' },
    ],
    setup: GOOGLE_SETUP,
    notes: [
      'Google no deja pasar una tarea recurrente a otra lista.',
      'Si no dices la lista, usa la principal.',
      CONFIRM_NOTE,
    ],
    links: GOOGLE_LINKS,
  },
  {
    id: 'google-contacts',
    name: 'Contactos de Google',
    category: 'contactos',
    status: 'available',
    icon: 'users',
    color: '#0b8043',
    summary: 'Busca, guarda y actualiza tus contactos, y te dice quién cumple años.',
    video: null,
    capabilities: [
      'Encontrar el correo o el teléfono de una persona por su nombre.',
      'Guardar contactos nuevos y agregarles correos o teléfonos.',
      'Saber quién cumple años en una fecha o un mes.',
      'Usar a tus contactos para escribirles o invitarlos a reuniones.',
    ],
    tools: [
      { id: 'buscar_contacto', name: 'Buscar contacto', description: 'Busca por nombre, correo o teléfono en tus contactos y en las personas con las que te has escrito.', effect: false, examples: ['¿Cuál es el correo de Camila?'] },
      { id: 'crear_contacto', name: 'Guardar contacto', description: 'Guarda una persona nueva con sus correos, teléfonos, empresa y notas.', effect: true, examples: ['Guarda a Diego, 300 123 4567'] },
      { id: 'actualizar_contacto', name: 'Actualizar contacto', description: 'Cambia el nombre, la empresa o las notas, o suma correos y teléfonos.', effect: true, examples: ['Agrégale a Diego el correo diego@empresa.com'] },
      { id: 'borrar_contacto', name: 'Borrar contacto', description: 'Elimina un contacto de tu cuenta de Google.', effect: true, examples: ['Borra el contacto de Diego'] },
      { id: 'ver_cumpleanos', name: 'Ver cumpleaños', description: 'Lista quién cumple años entre dos fechas, con la edad si Google la tiene.', effect: false, examples: ['¿Quién cumple años este mes?'] },
    ],
    requirements: GOOGLE_REQUIREMENTS,
    scopes: [
      { scope: 'contacts', access: 'Ver y editar tus contactos', use: 'Busca, guarda y actualiza los contactos que le indiques.' },
      { scope: 'contacts.other.readonly', access: 'Ver las personas con las que te has escrito (solo lectura)', use: 'Encuentra el correo de alguien que no tienes guardado pero con quien ya te escribiste.' },
    ],
    setup: GOOGLE_SETUP,
    notes: [
      'Solo los contactos guardados en tu cuenta se pueden cambiar o borrar.',
      'Si en una nota de voz confunde un nombre, corrígelo por escrito.',
      CONFIRM_NOTE,
    ],
    links: GOOGLE_LINKS,
  },
  {
    id: 'google-docs',
    name: 'Google Docs',
    category: 'documentos',
    status: 'available',
    icon: 'file',
    color: '#4285f4',
    summary: 'Crea documentos con lo que le dictes, léelos, complétalos y compártelos.',
    video: null,
    capabilities: [
      'Crear documentos de Google Docs con el texto que le dictes.',
      'Leer y completar los documentos que creó.',
      'Compartir documentos, hojas y formularios como lector, comentador o editor.',
      'Encontrar los archivos que creó por su nombre.',
    ],
    tools: [
      { id: 'crear_documento', name: 'Crear documento', description: 'Crea un documento de Google Docs con el texto que le das.', effect: true, examples: ['Hazme un documento con el acta de hoy'] },
      { id: 'leer_documento', name: 'Leer documento', description: 'Lee el texto de un documento que creó, sin imágenes ni formato.', effect: false, examples: ['Léeme el acta de ayer'] },
      { id: 'agregar_a_documento', name: 'Completar documento', description: 'Agrega párrafos al final de un documento que creó.', effect: true, examples: ['Agrega al acta: próxima reunión el lunes a las 9'] },
      { id: 'compartir_documento', name: 'Compartir archivo', description: 'Da permiso de lector, comentador o editor a un correo; Google le avisa.', effect: true, examples: ['Compártelo con ana@empresa.com como editora'] },
      { id: 'ver_documentos', name: 'Ver mis archivos', description: 'Lista los documentos, hojas y formularios que creó, del más reciente al más viejo.', effect: false, examples: ['¿Qué documentos me has creado esta semana?'] },
    ],
    requirements: [...GOOGLE_REQUIREMENTS.slice(0, 2), 'Que el conector Google Drive esté asignado a tu cuenta.'],
    scopes: [DRIVE_FILE_SCOPE],
    setup: GOOGLE_SETUP,
    notes: [DRIVE_NOTE, 'Los documentos se crean con texto plano, sin formato ni imágenes.', CONFIRM_NOTE],
    links: GOOGLE_LINKS,
  },
  {
    id: 'google-sheets',
    name: 'Google Sheets',
    category: 'documentos',
    status: 'available',
    icon: 'sheet',
    color: '#188038',
    summary: 'Crea hojas de cálculo con tus columnas y ve llenándolas por mensaje.',
    video: null,
    capabilities: [
      'Crear hojas de cálculo con las columnas que necesitas.',
      'Agregar filas dictándolas en una frase.',
      'Leer lo que llevas en una hoja.',
      'Encontrar las hojas que creó por su nombre.',
    ],
    tools: [
      { id: 'crear_hoja', name: 'Crear hoja', description: 'Crea una hoja de Google Sheets con las columnas de la primera fila.', effect: true, examples: ['Crea una hoja de gastos con fecha, concepto y valor'] },
      { id: 'agregar_filas', name: 'Agregar filas', description: 'Agrega filas después de la última con datos. Fechas y números quedan como en la hoja.', effect: true, examples: ['Agrega a gastos: hoy, almuerzo, 35 000'] },
      { id: 'leer_hoja', name: 'Leer hoja', description: 'Lee las filas de una hoja que creó.', effect: false, examples: ['¿Cuánto llevo gastado en la hoja de gastos?'] },
      { id: 'ver_hojas', name: 'Ver mis hojas', description: 'Lista las hojas de cálculo que creó, de la más reciente a la más vieja.', effect: false, examples: ['¿Qué hojas de cálculo tengo contigo?'] },
    ],
    requirements: [...GOOGLE_REQUIREMENTS.slice(0, 2), 'Que el conector Google Sheets esté asignado a tu cuenta.'],
    scopes: [DRIVE_FILE_SCOPE],
    setup: GOOGLE_SETUP,
    notes: [
      DRIVE_NOTE,
      'Un texto que empieza por «=» queda como texto, no como fórmula.',
      'Para compartir una hoja se usa «Compartir archivo» de Google Docs.',
      CONFIRM_NOTE,
    ],
    links: GOOGLE_LINKS,
  },
  {
    id: 'google-forms',
    name: 'Google Forms',
    category: 'documentos',
    status: 'available',
    icon: 'form',
    color: '#7248b9',
    summary: 'Arma encuestas y formularios, y te cuenta cuántos respondieron y qué.',
    video: null,
    capabilities: [
      'Crear formularios y encuestas publicados, listos para enviar.',
      'Agregar preguntas a un formulario que creó.',
      'Contar y resumir las respuestas.',
      'Abrir o cerrar un formulario, o mandarlo a la papelera.',
    ],
    tools: [
      { id: 'crear_formulario', name: 'Crear formulario', description: 'Crea un formulario publicado con sus preguntas, y te da el enlace para responder.', effect: true, examples: ['Arma una encuesta para el paseo con tres preguntas'] },
      { id: 'agregar_preguntas', name: 'Agregar preguntas', description: 'Agrega preguntas al final de un formulario que creó.', effect: true, examples: ['Agrega una pregunta de si traen acompañante'] },
      { id: 'leer_respuestas', name: 'Leer respuestas', description: 'Cuenta y resume las respuestas, y muestra las más recientes.', effect: false, examples: ['¿Cuántos han respondido la encuesta del paseo?'] },
      { id: 'cambiar_estado_formulario', name: 'Abrir o cerrar', description: 'Deja de aceptar respuestas, o vuelve a aceptarlas.', effect: true, examples: ['Cierra el formulario del paseo'] },
      { id: 'ver_formularios', name: 'Ver mis formularios', description: 'Lista los formularios que creó, del más reciente al más viejo.', effect: false, examples: ['¿Qué formularios tengo abiertos?'] },
      { id: 'borrar_formulario', name: 'Borrar formulario', description: 'Lo manda a la papelera de Drive, desde donde lo puedes recuperar.', effect: true, examples: ['Borra la encuesta del paseo'] },
    ],
    requirements: [...GOOGLE_REQUIREMENTS.slice(0, 2), 'Que el conector Google Forms esté asignado a tu cuenta.'],
    scopes: [DRIVE_FILE_SCOPE],
    setup: GOOGLE_SETUP,
    notes: [
      DRIVE_NOTE,
      'Las preguntas nuevas se agregan al final; las que ya existen no se editan ni se borran desde Secretario.',
      'Comparte siempre el enlace para responder, no el de edición.',
      CONFIRM_NOTE,
    ],
    links: GOOGLE_LINKS,
  },
  {
    id: 'recordatorios',
    name: 'Recordatorios y encargos',
    category: 'asistente',
    status: 'available',
    icon: 'bell',
    color: '#0e9f4f',
    summary: 'Avisos a una hora y encargos que Secretario hace por ti, una vez o repetidos.',
    video: null,
    capabilities: [
      'Recordatorios: solo te avisan, como «⏰ Recordatorio: …».',
      'Encargos: a esa hora, Secretario hace algo por ti y te cuenta el resultado.',
      'Una sola vez, todos los días, algunos días de la semana o un día de cada mes.',
      'Ver tus avisos y cancelar los que ya no necesitas.',
    ],
    tools: [
      { id: 'programar_aviso', name: 'Programar aviso', description: 'Crea un recordatorio o un encargo para una hora, una vez o repetido.', effect: true, examples: ['Recuérdame llamar al médico mañana a las 9', 'Recuérdame tomar la pastilla todos los días a las 8 pm', 'Todos los lunes a las 7 mándame mi agenda de la semana'] },
      { id: 'ver_avisos', name: 'Ver avisos', description: 'Te lista los recordatorios y encargos que tienes activos.', effect: false, examples: ['¿Qué avisos tengo?'] },
      { id: 'cancelar_aviso', name: 'Cancelar aviso', description: 'Quita un recordatorio o un encargo.', effect: true, examples: ['Cancela el de la pastilla'] },
    ],
    requirements: [
      'Tener acceso a Secretario: tu número de WhatsApp registrado.',
      'No necesita conectar ninguna cuenta. Un encargo que usa tu agenda o tu correo sí necesita esa herramienta.',
    ],
    scopes: [],
    setup: [
      'Pídele algo para una hora concreta o que se repita.',
      'Secretario elige si es un recordatorio o un encargo según lo que digas.',
      'Confirma con «sí» y queda programado.',
    ],
    notes: [
      'El texto del aviso es corto: hasta 200 caracteres.',
      'Hay un máximo de avisos activos y un plazo máximo hacia el futuro; si te pasas, Secretario te lo dice.',
      'Si llevas más de un día sin escribirle, el aviso te llega como un mensaje de plantilla de WhatsApp. Respóndele y siguen la conversación.',
    ],
    links: [{ label: 'Preferencias', href: '/secretario/aprende/preferencias' }],
  },
  {
    id: 'preferencias',
    name: 'Preferencias',
    category: 'asistente',
    status: 'available',
    icon: 'sliders',
    color: '#5128f5',
    summary: 'Enséñale cómo te gusta que trabaje, en general o para cada herramienta.',
    video: null,
    capabilities: [
      'Generales: cómo quieres que te hable o te responda.',
      'De una herramienta: reglas para el correo, el calendario o las tareas.',
      'Solo esta vez: un ajuste para un pedido, que no se guarda.',
      'Cambiar o borrar lo que le enseñaste.',
    ],
    tools: [
      { id: 'guardar_preferencias', name: 'Guardar preferencias', description: 'Guarda cómo quieres que trabaje, con frases como «de ahora en adelante…», «siempre…» o «recuerda que…».', effect: true, examples: ['De ahora en adelante, mis correos siempre formales y firmados como Ana', 'Recuerda que mis reuniones siempre llevan Meet', 'Borra mis preferencias de Gmail'] },
    ],
    requirements: ['Tener acceso a Secretario: tu número de WhatsApp registrado.', 'Para las preferencias de una herramienta, tenerla asignada.'],
    scopes: [],
    setup: [
      'Dile cómo te gusta que trabaje: «de ahora en adelante…», «siempre…» o «recuerda que…».',
      'Secretario te propone lo que va a guardar y espera tu «sí».',
      'Para un solo pedido, dilo en ese mensaje («este correo, más corto»): se aplica y no se guarda.',
    ],
    notes: ['Las preferencias tienen un largo máximo. Si te pasas, Secretario te lo dice.'],
    links: [{ label: 'Recordatorios y encargos', href: '/secretario/aprende/recordatorios' }],
  },

  // En la hoja de ruta: aún no funcionan. Se muestran solo en «Próximamente».
  {
    id: 'gmail-lectura',
    name: 'Leer y buscar en Gmail',
    category: 'correo',
    status: 'planned',
    icon: 'search',
    color: '#d93025',
    summary: 'Buscar, leer y resumir los correos que recibes. Requiere una evaluación de seguridad adicional de Google.',
  },
  {
    id: 'microsoft',
    name: 'Cuentas de Microsoft',
    category: 'correo',
    status: 'planned',
    icon: 'cloud',
    color: '#0078d4',
    summary: 'Conectar una cuenta de Microsoft, como Outlook, con sus propias herramientas.',
  },
  {
    id: 'google-maps',
    name: 'Google Maps',
    category: 'calendario',
    status: 'planned',
    icon: 'map',
    color: '#ea8600',
    summary: 'Tiempos de viaje y lugares, para la ubicación de tus eventos y avisarte cuándo salir.',
  },
]

export const AVAILABLE = CONNECTORS.filter((c) => c.status === 'available')
export const PLANNED = CONNECTORS.filter((c) => c.status === 'planned')

// Conectores disponibles de una categoría, en el orden del catálogo.
export const connectorsOf = (categoryId) => AVAILABLE.filter((c) => c.category === categoryId)

export const findConnector = (id) => AVAILABLE.find((c) => c.id === id)

export const categoryOf = (connector) => CATEGORIES.find((c) => c.id === connector.category)

// Total de herramientas disponibles, para las cifras de la portada.
// «1 herramienta», «5 herramientas»; `many` para plurales irregulares («conectores»).
export const plural = (n, word, many = `${word}s`) => `${n} ${n === 1 ? word : many}`

// Cuántos conectores disponibles y cuántas herramientas tiene una categoría.
export const categoryStats = (categoryId) => {
  const items = connectorsOf(categoryId)
  return { connectors: items.length, tools: items.reduce((n, c) => n + c.tools.length, 0) }
}

export const TOOL_COUNT = AVAILABLE.reduce((n, c) => n + c.tools.length, 0)

// Permisos de Google sin repetir, en el orden del catálogo, con los conectores que usan cada uno.
export const GOOGLE_SCOPES = AVAILABLE.reduce((list, c) => {
  c.scopes.forEach((s) => {
    const found = list.find((x) => x.scope === s.scope)
    if (found) found.products.push(c.name)
    else list.push({ ...s, products: [c.name] })
  })
  return list
}, [])
